"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TRASH_RETENTION_DAYS = exports.PUBLISH_GRACE_MS = exports.ONE_OFF_CAMPAIGN_NAME = exports.DEFAULT_BLOG_EXPORT_FORMAT = exports.BLOG_EXPORT_FORMATS = exports.BLOG_EXPORT_DESTINATION = exports.POST_METRICS_WINDOW_DAYS = void 0;
exports.isExportOnlyPost = isExportOnlyPost;
exports.neverSayWarning = neverSayWarning;
exports.isPostFailed = isPostFailed;
exports.isPostOverdue = isPostOverdue;
exports.canReleasePost = canReleasePost;
exports.contentEditableReason = contentEditableReason;
exports.scheduleLockReason = scheduleLockReason;
exports.campaignPostReopenFrom = campaignPostReopenFrom;
// ApprovalState/ApprovalType are VALUE imports: canReleasePost compares against their members.
const enums_1 = require("./enums");
/**
 * How far back the per-post metrics sweep looks. A published post older than this is never
 * refreshed again, so a client waiting on its metrics would wait forever — shared so the UI's
 * "is this still pending?" test and the server's eligibility rule cannot drift apart.
 */
exports.POST_METRICS_WINDOW_DAYS = 30;
/**
 * The blog destination that means "no live CMS — give me a file".
 *
 * A plain label like every other destination, because that is what the field is: free text the
 * composer offers a list for. Named here so the composer, the export endpoint and the publish gate
 * all mean the same string.
 */
exports.BLOG_EXPORT_DESTINATION = 'Export';
/**
 * A post that produces a FILE and publishes nowhere.
 *
 * Shared because four separate rules key on it — the schedule tab's banner, whether picking a time
 * advances the status, whether a missing time blocks approval, and whether scheduling announces
 * itself to the automation layer. Four copies of "channel is blog and label is Export" is how one of
 * them ends up disagreeing.
 */
function isExportOnlyPost(channel, platformLabel) {
    // Compared as a string: this module imports ContentChannel as a TYPE, and widening that to a value
    // import would pull the enums module into every consumer of the content contract.
    return channel === 'BLOG' && platformLabel === exports.BLOG_EXPORT_DESTINATION;
}
exports.BLOG_EXPORT_FORMATS = [
    { value: 'markdown', label: 'Markdown', extension: 'md' },
    { value: 'docx', label: 'Word doc', extension: 'docx' },
    { value: 'html', label: 'HTML', extension: 'html' },
];
exports.DEFAULT_BLOG_EXPORT_FORMAT = 'markdown';
/**
 * Name of the auto-created bucket that clusters campaign-less ("one-off") posts, one per
 * workspace. Shared so the server (which creates/looks it up by name) and the client (which
 * labels it) can never disagree.
 */
exports.ONE_OFF_CAMPAIGN_NAME = 'One-Off Posts';
/**
 * The one line that names Never say phrases still in generated text after one regeneration (Brand
 * Workspace v2; spec:514, O12). Shared so the server and the composer say the same thing:
 * - 1 term: `This draft uses "cure", which is on your brand's Never say list. Review it before you use it.`
 * - 2 terms: `This reply uses "cure" and "guarantee", which are on your brand's Never say list. …`
 * - 3 or more: `This wording uses "cure", "guarantee" and "miracle", which are on your brand's Never say list. …`
 *
 * Says "your brand" (spec:28). '' when there are no terms.
 */
function neverSayWarning(terms, noun) {
    if (terms.length === 0)
        return '';
    const quoted = terms.map((term) => `"${term}"`);
    const list = quoted.length === 1 ? quoted[0] : `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
    const verb = quoted.length === 1 ? 'is' : 'are';
    return `This ${noun} uses ${list}, which ${verb} on your brand's Never say list. Review it before you use it.`;
}
/**
 * How long past its scheduled time a post may sit unpublished before it counts as FAILED.
 *
 * A grace window is required, not optional: the release worker ticks once a minute and a crashed
 * delivery is re-claimable only after PUBLISH_CLAIM_TTL_MS (10 min), so a post is legitimately
 * "late but fine" for several minutes. This is comfortably past that, and short enough that a
 * genuinely stuck post surfaces quickly.
 *
 * Shared so the UI's Failed tab and the worker's don't-publish-it-late guard use ONE number —
 * if they drifted, a post could show as Failed and then publish anyway.
 */
exports.PUBLISH_GRACE_MS = 15 * 60 * 1000;
/**
 * A post has FAILED when it was due to go out and didn't: either delivery was attempted and
 * errored, or its scheduled time passed by more than the grace window while it sat unpublished.
 * Anything already published is never failed.
 */
function isPostFailed(post, now = Date.now()) {
    if (post.publishedAt)
        return false;
    if (post.publishError)
        return true;
    if (!post.scheduledAt)
        return false;
    const at = post.scheduledAt instanceof Date ? post.scheduledAt.getTime() : Date.parse(post.scheduledAt);
    return Number.isFinite(at) && at < now - exports.PUBLISH_GRACE_MS;
}
/**
 * A post whose scheduled time came and went with nothing publishing it.
 *
 * NOT the same thing as a delivery failure, which is why it has its own name. Nothing was ever
 * attempted here: the time simply passed while the post waited — almost always for an approval that
 * arrived too late — so there is no platform error to show and nothing to retry. The only fix is a
 * new time, which is why the UI calls it a scheduling error rather than a failure.
 *
 * Same grace window as {@link isPostFailed}, deliberately: a post two minutes past its slot may
 * still go out on this minute's tick, and telling someone to reschedule something that is about to
 * publish is worse than saying nothing.
 */
function isPostOverdue(post, now = Date.now()) {
    if (post.publishedAt)
        return false;
    // A recorded error means delivery WAS attempted. That is a failure, and it keeps that name.
    if (post.publishError)
        return false;
    if (!post.scheduledAt)
        return false;
    const at = post.scheduledAt instanceof Date ? post.scheduledAt.getTime() : Date.parse(post.scheduledAt);
    return Number.isFinite(at) && at < now - exports.PUBLISH_GRACE_MS;
}
/** How long a trashed Campaign/Post is recoverable before it is permanently purged. */
exports.TRASH_RETENTION_DAYS = 30;
/**
 * May this post be published right now?
 *
 * Shared because the copy of this rule in the UI had drifted from the one in `posts.service.ts`,
 * and the drift was invisible: the UI's comment said it mirrored the server exactly while it had
 * actually grown STRICTER, hiding a publish the API would have accepted. One function means the
 * menu can only offer what the server will take, and can't silently stop offering what it will.
 *
 * The rule:
 *  · Already published — never again, in any mode.
 *  · A recorded `publishError` releases the gate. A failure means a release was AUTHORISED and
 *    attempted (every delivery path runs this gate first, then records the platform's own reason),
 *    so what failed was delivery — an expired token, rejected media, a depleted balance — not the
 *    approval. Retrying resumes that authorised action; it does not grant a new one. Without this
 *    a retry cannot exist in CAMPAIGN mode at all: the campaign's aggregate moves to PUBLISHED as
 *    its other posts go out, stranding the failed one behind a gate that can never reopen.
 *    But only while the post still holds what was approved: its own state still APPROVED or
 *    SCHEDULED, PUBLISHED (the label a lane action copies from a published campaign onto every
 *    post that had none - a failed one included), or none at all, inheriting. A post reopened
 *    since — back to DRAFT to be edited, or into review again — owes a new approval, and its old
 *    failure authorises nothing: otherwise reopen, rewrite and "Retry" would publish copy nobody
 *    signed off.
 *  · CAMPAIGN mode — the campaign's aggregate OR the post's own state may authorise it (under
 *    lanes, the post's own state IS its lane's state).
 *  · POST mode — the post's own state alone.
 *  · NONE mode — no approval gate at all.
 *
 * Deliberately NOT keyed on `scheduledAt`: a post may carry a PROPOSED time while still a draft,
 * and that must never be mistaken for approval. Equally, NOT keyed on `isPostFailed`, which also
 * counts a post that merely sailed past its time — never attempted, never approved, and still
 * owing an approval.
 */
function canReleasePost(post, campaignState, approvalType) {
    if (post.publishedAt)
        return false;
    const ready = (s) => s === enums_1.ApprovalState.APPROVED || s === enums_1.ApprovalState.SCHEDULED;
    const heldSinceRelease = (s) => s == null || ready(s) || s === enums_1.ApprovalState.PUBLISHED;
    if (post.publishError && heldSinceRelease(post.approvalState))
        return true;
    const postState = post.approvalState ?? enums_1.ApprovalState.DRAFT;
    if (approvalType === enums_1.ApprovalType.CAMPAIGN)
        return ready(campaignState) || ready(postState);
    if (approvalType === enums_1.ApprovalType.POST)
        return ready(postState);
    return true; // NONE
}
/**
 * Why a post's content cannot be written, or null when it can.
 *
 * Shared by the API (every content writer asks it) and the composer (which locks the form on it), so
 * the form can never offer an edit the server refuses, or refuse one the server would take.
 *
 * CAMPAIGN mode reads the POST's own state when it has one, because that state IS its lane's, and
 * lanes are approved separately. Reading only the campaign meant an APPROVED social lane stayed
 * fully editable — the campaign sits at the least-mature lane, so a still-drafting blog lane kept
 * the whole campaign at DRAFT and nothing was ever locked. Someone could rewrite copy an approver
 * had signed off, and it would publish. (The reverse bit too: a campaign stays PUBLISHED once its
 * destinations have gone out or failed, so reading it locked a failed post its lane had reopened.)
 *
 * Null falls back to the campaign, which is exactly what an untouched campaign-mode post inherits.
 * The POST branch keeps its own default: a null there means a post that never entered a lifecycle,
 * and treating it as the campaign's would refuse edits inside an approved campaign after a
 * NONE→POST flip, which does not run the seeding backfill.
 * NONE mode refuses nothing — there is no approval to protect. Explicit, because a NONE workspace
 * CAN hold posts with a non-null state left over from a prior POST configuration, and now that the
 * campaign branch reads the post's own state those posts would suddenly be uneditable in a
 * workspace that has no approvals at all.
 */
function contentEditableReason(mode, postState, campaignState) {
    if (mode === enums_1.ApprovalType.NONE)
        return null;
    const state = mode === enums_1.ApprovalType.POST
        ? (postState ?? enums_1.ApprovalState.DRAFT)
        : (postState ?? campaignState);
    if (state === enums_1.ApprovalState.DRAFT || state === enums_1.ApprovalState.REVISE)
        return null;
    return `The ${mode === enums_1.ApprovalType.POST ? 'post' : 'owning campaign'} must be DRAFT or REVISE to edit`;
}
const SCHEDULABLE_STATES = new Set([
    enums_1.ApprovalState.DRAFT,
    enums_1.ApprovalState.REVISE,
    enums_1.ApprovalState.APPROVED,
    enums_1.ApprovalState.SCHEDULED,
]);
/**
 * Why a post's time cannot be set or changed, or null when it can.
 *
 * A PROPOSED time may be set while the post is editable (DRAFT/REVISE) — it is required before a
 * post or lane can be submitted for review — and the same call releases an approved post (APPROVED →
 * SCHEDULED). Only the mid-review states lock it: the proposed time is part of what was submitted.
 *
 * The state that governs is the one {@link contentEditableReason} reads: the post's own (its lane's)
 * in CAMPAIGN mode, falling back to the campaign's. Reading the campaign's alone kept a reopened
 * post from ever getting a time again once its campaign had been released — and without a time its
 * lane could never be submitted again.
 */
function scheduleLockReason(mode, postState, campaignState) {
    if (mode === enums_1.ApprovalType.NONE)
        return null;
    const state = mode === enums_1.ApprovalType.POST
        ? (postState ?? enums_1.ApprovalState.DRAFT)
        : (postState ?? campaignState);
    if (SCHEDULABLE_STATES.has(state))
        return null;
    const what = mode === enums_1.ApprovalType.POST || postState != null ? 'This post' : 'This campaign';
    if (state === enums_1.ApprovalState.PUBLISHED)
        return `${what} has already been published`;
    if (state === enums_1.ApprovalState.REJECTED)
        return `${what} was rejected — reopen it to give it a new time`;
    return what === 'This post'
        ? "This post is under review — its schedule is locked until it's approved or reopened"
        : "This campaign is under review — its posts' schedules are locked until it's approved or reopened";
}
/**
 * The state a CAMPAIGN-mode post is reopened from when it is reopened on its own - back to DRAFT, to
 * be edited and approved again - or null when it can't be: published, already editable, or under
 * review (that goes back with its lane, on the campaign page).
 *
 * Shared by the API's reopenForEditing and the composer's "Reopen this post", so the button shows
 * exactly when the server will take it.
 *
 * A post whose delivery failed is approved content that never went out, whatever its release left on
 * it: no state of its own (released with its campaign, so approved), or PUBLISHED, which a lane
 * action copies from a published campaign onto every post that had none.
 */
function campaignPostReopenFrom(post) {
    if (post.publishedAt)
        return null;
    const own = post.approvalState ?? null;
    if (own === enums_1.ApprovalState.APPROVED)
        return enums_1.ApprovalState.APPROVED;
    if (own === enums_1.ApprovalState.SCHEDULED)
        return enums_1.ApprovalState.SCHEDULED;
    if (!post.publishError)
        return null;
    if (own == null)
        return enums_1.ApprovalState.APPROVED;
    if (own === enums_1.ApprovalState.PUBLISHED)
        return enums_1.ApprovalState.PUBLISHED;
    return null;
}
//# sourceMappingURL=content.js.map